import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { LocaleService } from '@finder/shared/services/locale-service/locale-service.service';
import { LocaleHelperService } from '@finder/shared/services/locale-service/locale-helper.service';
import { IPicture } from '@finder/shared/components/picture/picture.interface';
import { LOCALES } from '@finder/shared/constants/app.constants';

import { SEARCH_CARD_TYPE, SEARCH_PARK_TYPE, SEARCH_SCHEDULE_TYPE } from './search-card.constants';

import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import isArray from 'lodash-es/isArray';

import { SearchSelection } from '@finder/features/search/interfaces/search-selection.interface';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { AnchorComponent } from '@finder/shared/components/anchor/anchor.component';
import { CommonModule } from '@angular/common';
import { PictureComponent } from '@finder/shared/components/picture/picture.component';
import { FontIconComponent } from '@finder/shared/components/font-icon/font-icon.component';
import { TruncateTextPipe } from '@finder/shared/utils/pipes/truncate-text/truncate-text.pipe';
import { SafeHtmlPipe } from '@finder/shared/utils/pipes/safe-html/safe-html.pipe';
import { OperatingStatusComponent } from '@finder/shared/components/operating-status/operating-status.component';
import { PremierAccessComponent } from '@finder/shared/components/premier-access/premier-access.component';

/**
 * @SSR:
 * This component has some html issues that SSR doesn't like
 * Nested anchor tags
 * finder-anchor => finder-anchor
 * https://v18.angular.dev/guide/hydration#valid-html-structure
 *
 * There seems to be some issue with the safeHtml replacement that triggers an NG0500 error
 * https://angular.dev/errors/NG0500
 *
 * It doesn't matter that much because search won't run through SSR but I wanted to make a note
 * incase that changes
 */

@Component({
    selector: 'finder-search-card',
    templateUrl: './search-card.component.html',
    styleUrls: ['./search-card.component.scss'],
    standalone: true,
    imports: [
        AnchorComponent,
        CommonModule,
        FontIconComponent,
        OperatingStatusComponent,
        PictureComponent,
        PremierAccessComponent,
        SafeHtmlPipe,
        TruncateTextPipe,
    ]
})
export class SearchCardComponent implements OnInit {

    @Input() searchResult: any;
    @Input() searchTerm: any;
    @Input() schedulesLabels: any;
    @Input() searchPosition: any;
    @Input() searchTarget = '_self';
    @Output() searchSelection = new EventEmitter();

    disneyParkBlog = SEARCH_CARD_TYPE.disneyParkBlog;
    planDisney = SEARCH_CARD_TYPE.planDisney;
    faqDisney = SEARCH_CARD_TYPE.faq;
    scheduleFilterArray = [SEARCH_SCHEDULE_TYPE.performanceTime, SEARCH_SCHEDULE_TYPE.operating, SEARCH_SCHEDULE_TYPE.closed];
    displaySchedules = false;
    scheduleTimes = '';
    picture: IPicture;
    parkType: any;
    scheduleServiceLabel: string;
    shipLocations: any;
    blogpostDetail: any;
    planDetail: any;
    faqDetail: any;
    hasSchedule = false;

    constructor(
        private localeService: LocaleService,
        private localeHelper: LocaleHelperService,
        private windowRef: WindowRef
    ) { }

    ngOnInit() {
        this.searchType();
    }

    searchType(): void {
        switch (this.searchResult.type) {
            case this.disneyParkBlog:
                this.getBlogDetail();
                break;
            case this.planDisney:
                this.getPlanDetail();
                break;
            case this.faqDisney:
                this.getFaqDetail();
                break;
            default:
                this.defaultDetail();
                break;
        }
    }

    sendSearchSelection() {
        const searchSelectionOptions: SearchSelection = {
            searchId: get(this.searchResult, 'searchId', ''),
            documentId: get(this.searchResult, 'id', ''),
            title: get(this.searchResult, 'title', ''),
            position: this.searchPosition,
            pageUrl: get(this.searchResult, 'url', ''),
        };
        this.searchSelection.emit(searchSelectionOptions);
    }

    private getFaqDetail(): void {
        this.faqDetail = { ...this.searchResult };
        this.faqDetail.title = `${this.faqDetail.title?.split('|')[0]} - ${this.faqDetail.label}`;

        const isValidUrl = get(this.faqDetail, 'faqTopicUrl', false) && get(this.faqDetail, 'urlFriendlyId', false);

        if (isValidUrl) {
            const domain = this.windowRef.nativeWindow.location.origin;
            const path = `/${this.faqDetail.type}/${this.faqDetail.faqTopicUrl}/${this.faqDetail.urlFriendlyId}`;
            this.searchResult.url = new URL(`${domain}${path}`).href;
        }
    }

    private getPlanDetail(): void {
        this.planDetail = { ...this.searchResult };

        const questionTemplate = get(this.planDetail, 'questionTitleTemplate');
        const answerTemplate = get(this.planDetail, 'answerTitleTemplate');
        const seeMoreUrlTemplate = get(this.planDetail.seeMore, 'urlTemplate');

        this.planDetail.questionTitleTemplate = this.replaceString(questionTemplate, this.searchResult);
        this.planDetail.seeMore.urlTemplate = this.replaceString(seeMoreUrlTemplate, { TERM: this.searchTerm });
        this.planDetail.answerTitleTemplate = this.replaceString(answerTemplate, this.searchResult);
        this.planDetail.logo = this.getCardLogo(this.planDetail);
    }

    private getBlogDetail(): void {
        this.blogpostDetail = { ...this.searchResult };
        const postTemplate = get(this.blogpostDetail, 'postDetailTemplate');

        let locale = this.localeService.getLocale();
        locale = locale ? this.localeHelper.getNormalizedLocale(locale) : this.localeHelper.getNormalizedLocale(LOCALES.en_US_normalized);

        this.blogpostDetail.postDate = new Date(this.blogpostDetail.postDate)
            .toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });

        this.blogpostDetail.postDetailTemplate = this.replaceString(postTemplate, this.blogpostDetail);
        this.blogpostDetail.logo = this.getCardLogo(this.blogpostDetail);
    }

    private getCardLogo(searchResult: any): any {
        const image = get(searchResult, 'logo');
        if (image) {
            return {
                alt: searchResult.type,
                mobile: image
            };
        }
    }

    private getCardImage(): void {
        const image = get(this.searchResult, 'media');
        if (image) {
            this.picture = {
                alt: image.alt,
                mobile: image.url
            };
        }
    }

    private getParkType(): void {
        const type = this.searchResult.resort || this.searchResult.themePark || {};
        if (!isEmpty(type)) {
            const urlType = !isEmpty(this.searchResult.resort) ? SEARCH_PARK_TYPE.resort : SEARCH_PARK_TYPE.destination;
            this.parkType = {
                name: get(type, 'name'),
                url: `${urlType}/${type.id}`
            };
        }
    }

    private replaceString(template: string, data: any): string {
        const templateRegex = /\{(.+?)\}/g;

        return template.replace(
            templateRegex,
            (match, tag) => data[tag.trim()]
        );
    }

    private defaultDetail() {
        if (this.searchResult.portUrl) {
            this.searchResult.portUrl = `ports/${this.searchResult.portUrl}`;
        }

        if (this.searchResult.shipLocations) {
            const ships = this.searchResult.shipLocations;
            this.shipLocations = isArray(ships) ?
                ships.map(locations => this.formatShipLocation(locations)) : [this.formatShipLocation(ships)];
        }

        this.searchResult.land = isArray(this.searchResult.land) ? this.searchResult.land[0] : this.searchResult.land;
        this.searchResult.experiences = isArray(this.searchResult.experiences) ?
            this.searchResult.experiences.join(', ') : this.searchResult.experiences;

        this.getCardImage();
        this.getParkType();
    }

    private formatShipLocation(locations: string) {
        const shipInfo = locations.split('|');

        return {
            name: shipInfo[0],
            url: shipInfo[1]
        };
    }
}
